/**
 * Hook for managing PostHog Early Access feature enrollment.
 *
 * Provides functions to enable/disable early access features and check enrollment status.
 */

import { useState, useCallback } from 'react';
import { PostHogManager } from '#~/posthog/manager.js';
import { PostHogFeatureFlag } from '#~/posthog/constants.js';
import { useFeatureFlags } from './use-feature-flags.js';

/** Early access feature types */
export type EarlyAccessFeature = 'recording' | 'output';

/** Map feature types to their PostHog flag keys (single or multiple flags) */
const EARLY_ACCESS_FLAG_MAP: Record<EarlyAccessFeature, PostHogFeatureFlag | PostHogFeatureFlag[]> =
  {
    recording: PostHogFeatureFlag.EARLY_ACCESS_RECORDING,
    output: [PostHogFeatureFlag.EARLY_ACCESS_OUTPUT, PostHogFeatureFlag.EARLY_ACCESS_RTMP]
  } as const;

/** Normalize flag(s) to array */
const normalizeFlags = (flags: PostHogFeatureFlag | PostHogFeatureFlag[]): PostHogFeatureFlag[] =>
  Array.isArray(flags) ? flags : [flags];

/** Resolve enrollment without making one output capability authoritative for another. */
export const isEarlyAccessFeatureEnabled = (
  flagKeys: readonly PostHogFeatureFlag[],
  values: Readonly<Record<string, boolean | undefined>>
): boolean => flagKeys.some((flag) => values[flag] === true);

/** Analytics events for early access */
export const EARLY_ACCESS_EVENTS = {
  RECORDING_ENABLED: 'early_access_recording_enabled',
  RECORDING_DISABLED: 'early_access_recording_disabled',
  OUTPUT_ENABLED: 'early_access_output_enabled',
  // ############################################################
  OUTPUT_DISABLED: 'early_access_output_disabled',
  CLICKED_RECORDING_BUTTON: 'clicked_recording_button',
  CLICKED_OUTPUT_BUTTON: 'clicked_output_button'
} as const;

export interface UseEarlyAccessReturn {
  /** Whether the early access feature is enabled */
  isEnabled: boolean;
  /** Whether the status is still loading */
  loading: boolean;
  /** Error if any occurred */
  error: Error | null;
  /** Enable early access for this feature */
  enable: () => Promise<void>;
  /** Disable early access for this feature */
  disable: () => Promise<void>;
  /** Toggle early access for this feature */
  toggle: () => Promise<void>;
}

/**
 * Hook to manage early access enrollment for a specific feature.
 *
 * @param feature - The early access feature to manage ('recording' or 'output')
 * @returns Object with enrollment status and control functions
 *
 * @example
 * ```typescript
 * const { isEnabled, enable, disable, loading } = useEarlyAccess('recording');
 *
 * if (!isEnabled) {
 *   return <button onClick={enable}>Enable Recording (Early Access)</button>;
 * }
 * ```
 */
export function useEarlyAccess(feature: EarlyAccessFeature): UseEarlyAccessReturn {
  const flagKeys = normalizeFlags(EARLY_ACCESS_FLAG_MAP[feature]);
  const { flags, loading, error } = useFeatureFlags(flagKeys, {
    enableRealtime: true
  });
  // Output enrollment spans independent NDI and RTMP controls. Either real ON
  // value exposes its own capability; missing or unavailable siblings stay OFF.
  const isEnabled = isEarlyAccessFeatureEnabled(flagKeys, flags);

  const [isUpdating, setIsUpdating] = useState(false);

  const updateEnrollment = useCallback(
    async (enroll: boolean) => {
      const manager = PostHogManager.getInstance();
      const posthog = manager.getPostHog();

      if (!posthog) {
        throw new Error('PostHog not initialized');
      }

      setIsUpdating(true);

      try {
        // Enroll/unenroll all flags for this feature
        for (const flag of flagKeys) {
          posthog.updateEarlyAccessFeatureEnrollment?.(flag, enroll);
        }

        const eventName =
          feature === 'recording'
            ? enroll
              ? EARLY_ACCESS_EVENTS.RECORDING_ENABLED
              : EARLY_ACCESS_EVENTS.RECORDING_DISABLED
            : enroll
              ? EARLY_ACCESS_EVENTS.OUTPUT_ENABLED
              : EARLY_ACCESS_EVENTS.OUTPUT_DISABLED;

        manager.capture(eventName, { feature, enrolled: enroll });

        // ensuring UI updates
        await manager.refreshFlags();
      } finally {
        setIsUpdating(false);
      }
    },
    [feature, flagKeys]
  );

  const enable = useCallback(() => updateEnrollment(true), [updateEnrollment]);
  const disable = useCallback(() => updateEnrollment(false), [updateEnrollment]);
  const toggle = useCallback(() => updateEnrollment(!isEnabled), [updateEnrollment, isEnabled]);

  return {
    isEnabled: isEnabled ?? false,
    loading: loading || isUpdating,
    error,
    enable,
    disable,
    toggle
  };
}

/**
 * Hook to check if all early access features are enabled.
 * Useful for checking overall early access status.
 */
export function useAllEarlyAccessEnabled(): {
  recording: boolean;
  output: boolean;
  allEnabled: boolean;
  loading: boolean;
} {
  const recording = useEarlyAccess('recording');
  const output = useEarlyAccess('output');

  return {
    recording: recording.isEnabled,
    output: output.isEnabled,
    allEnabled: recording.isEnabled && output.isEnabled,
    loading: recording.loading || output.loading
  };
}
